import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { XIcon } from './Icons';

// Hamburger menu icon
const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Close icon
const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export function Nav() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isHome = location.pathname === '/';
  const isConstruction = location.pathname === '/construction';
  const isAgents = location.pathname === '/agents';
  const isIndustriEst = location.pathname === '/industriest';

  const closeMenu = () => setMobileMenuOpen(false);

  const handleContactClick = (e) => {
    e.preventDefault();
    closeMenu();
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className="nav">
      <div className="nav-container">
        <Link to="/" className="logo" style={{ textDecoration: 'none' }}>
          <span className="logo-text">whatif<span className="logo-accent">WE</span></span>
          <span className="logo-subtitle">Custom AI Solutions</span>
        </Link>
        
        {/* Desktop nav */}
        <div className="nav-links nav-desktop">
          <Link to="/" className={isHome ? 'nav-active' : ''}>Home</Link>
          <Link to="/construction" className={isConstruction ? 'nav-active' : ''}>Construction AI</Link>
          <Link to="/industriest" className={isIndustriEst ? 'nav-active' : ''}>IndustriEst</Link>
          <Link to="/agents" className={isAgents ? 'nav-active' : ''}>Agent Deployments</Link>
          <a href="#contact" onClick={handleContactClick}>Contact</a>
          <a href="https://x.com/whatifwellcai" target="_blank" rel="noopener noreferrer" className="nav-x-link" title="@whatifwellcai">
            <XIcon /> <span>@whatifwellcai</span>
          </a>
        </div>

        {/* Mobile hamburger button */}
        <button 
          className="mobile-menu-btn" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
      </div>

      {/* Mobile menu overlay */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <Link to="/" className={isHome ? 'nav-active' : ''} onClick={closeMenu}>Home</Link>
        <Link to="/construction" className={isConstruction ? 'nav-active' : ''} onClick={closeMenu}>Construction AI</Link>
        <Link to="/industriest" className={isIndustriEst ? 'nav-active' : ''} onClick={closeMenu}>IndustriEst</Link>
        <Link to="/agents" className={isAgents ? 'nav-active' : ''} onClick={closeMenu}>Agent Deployments</Link>
        <a href="#contact" onClick={handleContactClick}>Contact</a>
        <a href="https://x.com/whatifwellcai" target="_blank" rel="noopener noreferrer" className="nav-x-link" onClick={closeMenu}>
          <XIcon /> @whatifwellcai
        </a>
      </div>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-brand">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <span className="logo-text">whatif<span className="logo-accent">WE</span></span>
            </Link>
            <p>Custom AI Software Solutions</p>
          </div>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <Link to="/construction">Construction AI</Link>
            <Link to="/industriest">IndustriEst</Link>
            <Link to="/agents">Agent Deployments</Link>
            <a href="#contact" onClick={(e) => {
              e.preventDefault();
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}>Contact</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 whatifWE LLC. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

export function ContactSection() {
  const [formData, setFormData] = React.useState({ name: '', email: '', company: '', service: '', message: '' });
  const [formStatus, setFormStatus] = React.useState({ sending: false, sent: false, error: null });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ sending: true, sent: false, error: null });
    try {
      const res = await fetch('https://whatifwellc.ai/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setFormStatus({ sending: false, sent: true, error: null });
        setFormData({ name: '', email: '', company: '', service: '', message: '' });
      } else {
        setFormStatus({ sending: false, sent: false, error: 'Failed to send' });
      }
    } catch (err) {
      setFormStatus({ sending: false, sent: false, error: 'Failed to send' });
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="contact-content">
          <div className="contact-info">
            <h2>Let's build something</h2>
            <p>
              Tell us about your business, your tools, and what you wish you had more time for. We'll design a solution that fits your world.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <h4>Email</h4>
                <p>inquiries@whatifwellc.ai</p>
              </div>
              <div className="contact-item">
                <h4>What We Build</h4>
                <p>Custom AI Software • Agent Deployments • Construction AI • Data Integration • Secure Infrastructure</p>
              </div>
            </div>
          </div>
          <div className="contact-form">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <input type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <input type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <input type="text" name="company" placeholder="Company" value={formData.company} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <select name="service" value={formData.service} onChange={handleInputChange}>
                  <option value="">What are you looking for?</option>
                  <option value="IndustriEst">IndustriEst Demo</option>
                  <option value="Custom AI Software">Custom AI Software</option>
                  <option value="Personal AI Agent">Personal AI Agent Deployment</option>
                  <option value="Construction AI">Construction AI Solutions</option>
                  <option value="Data Integration">Data Architecture & Integration</option>
                  <option value="Other">Something Else</option>
                </select>
              </div>
              <div className="form-group">
                <textarea name="message" placeholder="Tell us about your project..." rows="4" value={formData.message} onChange={handleInputChange}></textarea>
              </div>
              {formStatus.sent && <p style={{color: '#4ade80', marginBottom: '1rem'}}>Message sent successfully!</p>}
              {formStatus.error && <p style={{color: '#f87171', marginBottom: '1rem'}}>{formStatus.error}</p>}
              <button type="submit" className="btn btn-primary" disabled={formStatus.sending}>
                {formStatus.sending ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
